import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { api, qs } from "@/lib/api";
import { formatCents, formatDate, formatInt } from "@/lib/format";
import { useIsAdmin } from "@/lib/session";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Product } from "@/features/billing/catalog";
import { useContactNames } from "@/features/billing/useContactNames";
import {
  COMMISSION_STATUS,
  HOLD_DAYS,
  methodLabel,
  PARTNER_STATUS,
  payableFrom,
  pct,
  useAllAffiliates,
  usePrograms,
  type Affiliate,
  type Commission,
  type Payout,
  type RateOverride,
} from "./affiliates";
import { PartnerDialog } from "./PartnerDialog";
import { PayoutDialog } from "./PayoutDialog";
import { LoadError } from "@/components/LoadError";

export type AffiliatesTab = "commissions" | "payouts" | "programs";

/** Affiliates: partners with balances, commissions, payouts (record one: money, admins only) and programs. */
export function AffiliatesPage() {
  const { tab = "partners" } = useSearch({ from: "/affiliates" });
  const navigate = useNavigate({ from: "/affiliates" });
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader title="Affiliates" sub="Partners who refer buyers, and what they've earned">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          A partner earns a commission on each payment from someone they referred (and their recruiter earns the second level). Commissions wait {HOLD_DAYS} days for refunds
          before they can be paid; a refund takes its commission back. Partners see their own balances, links and payout details at account.&lt;brand&gt;.com/partners.
          Times are Mountain Time.
        </p>
      </PageHeader>
      <Tabs value={tab} onValueChange={(t) => navigate({ search: { tab: t === "partners" ? undefined : (t as AffiliatesTab) }, replace: true })}>
        <TabsList>
          <TabsTrigger value="partners">Partners</TabsTrigger>
          <TabsTrigger value="commissions">Commissions</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
          <TabsTrigger value="programs">Programs</TabsTrigger>
        </TabsList>
        <TabsContent value="partners" className="pt-3">
          <PartnersTab />
        </TabsContent>
        <TabsContent value="commissions" className="pt-3">
          <CommissionsTable />
        </TabsContent>
        <TabsContent value="payouts" className="pt-3">
          <PayoutsTable />
        </TabsContent>
        <TabsContent value="programs" className="pt-3">
          <ProgramsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PartnersTab() {
  const isAdmin = useIsAdmin();
  const [status, setStatus] = useState("");
  const [owed, setOwed] = useState(false);
  const [paying, setPaying] = useState<Affiliate | null>(null);
  const [adding, setAdding] = useState(false);
  const all = useAllAffiliates();
  const programs = usePrograms().data?.programs ?? [];
  const partners = all.data ?? [];
  const rows = partners.filter((a) => (!status || a.status === status) && (!owed || a.payable_cents > 0));
  const name = useContactNames(partners.map((a) => a.contact_id));
  const codeOf = new Map(partners.map((a) => [a.id, a.code]));
  const programOf = new Map(programs.map((p) => [p.id, p.name]));
  const totalPayable = partners.reduce((s, a) => s + Math.max(0, a.payable_cents), 0);
  return (
    <Card className="overflow-x-auto">
      <div className="flex flex-wrap items-center gap-2 border-b p-3">
        <Select className="h-8 w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">Any status</option>
          {Object.entries(PARTNER_STATUS).map(([k, [label]]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-1.5 text-sm">
          <Checkbox checked={owed} onChange={(e) => setOwed(e.target.checked)} />
          Only partners with money to pay
        </label>
        {partners.some((a) => a.status === "pending") && status !== "pending" && (
          <Button size="sm" variant="outline" onClick={() => setStatus("pending")}>
            {partners.filter((a) => a.status === "pending").length} waiting for approval
          </Button>
        )}
        {partners.length > 0 && (
          <span className="text-sm text-[var(--color-muted-foreground)]">
            Payable now across all partners: <span className="num font-medium text-[var(--color-foreground)]">{formatCents(totalPayable)}</span>
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {!isAdmin && <span className="text-xs text-[var(--color-warning-fg)]">Only an admin can record payouts.</span>}
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="size-4" /> New partner
          </Button>
        </span>
      </div>
      {all.isLoading && <Skeleton className="m-3 h-32" />}
      {all.error && <p className="p-3 text-sm text-[var(--color-danger-fg)]">{all.error.message}</p>}
      {!all.isLoading && !all.error && rows.length === 0 && <Empty title={partners.length ? "No partners match." : "No affiliate partners yet."} />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Partner</TH>
              <TH>Status</TH>
              <TH>Program</TH>
              <TH className="text-right">Earned</TH>
              <TH className="text-right">Unpaid</TH>
              <TH className="text-right">Payable now</TH>
              <TH className="text-right">Paid</TH>
              <TH className="text-right">Clicks</TH>
              {isAdmin && <TH className="w-px" />}
            </tr>
          </THead>
          <tbody>
            {rows.map((a) => {
              const [label, variant] = PARTNER_STATUS[a.status] ?? [a.status, "muted" as const];
              return (
                <TR key={a.id}>
                  <TD className="max-w-[16rem]">
                    <Link to="/affiliates/$affiliateId" params={{ affiliateId: String(a.id) }} className="font-medium text-[var(--color-primary)] hover:underline">
                      {a.code}
                    </Link>
                    <div className="truncate text-xs text-[var(--color-muted-foreground)]">
                      {name(a.contact_id)}
                      {a.parent_affiliate_id ? `, recruited by ${codeOf.get(a.parent_affiliate_id) ?? `#${a.parent_affiliate_id}`}` : ""}
                    </div>
                  </TD>
                  <TD>
                    <Badge variant={variant}>{label}</Badge>
                  </TD>
                  <TD className="text-xs">{a.default_program_id ? (programOf.get(a.default_program_id) ?? `#${a.default_program_id}`) : "—"}</TD>
                  <TD className="num text-right">{formatCents(a.earned_cents)}</TD>
                  <TD className="num text-right">{formatCents(a.unpaid_cents)}</TD>
                  <TD className="num text-right font-medium">{formatCents(a.payable_cents)}</TD>
                  <TD className="num text-right">{formatCents(a.paid_cents)}</TD>
                  <TD className="num text-right">{formatInt(a.clicks)}</TD>
                  {isAdmin && (
                    <TD>
                      {a.payable_cents > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => setPaying(a)}>
                          Record payout
                        </Button>
                      )}
                    </TD>
                  )}
                </TR>
              );
            })}
          </tbody>
        </Table>
      )}
      {paying && <PayoutDialog affiliate={paying} name={name(paying.contact_id)} onClose={() => setPaying(null)} />}
      {adding && <PartnerDialog partners={partners} onClose={() => setAdding(false)} />}
    </Card>
  );
}

/** Commissions, newest first; with affiliateId, only that partner's (the partner page). */
export function CommissionsTable({ affiliateId }: { affiliateId?: number }) {
  const [status, setStatus] = useState("");
  const q = useInfiniteQuery({
    queryKey: ["affiliate-commissions", affiliateId ?? 0, status],
    queryFn: ({ pageParam }) =>
      api<{ commissions: Commission[] }>(
        "/api/v1/affiliate-commissions" + qs({ affiliate_id: affiliateId, status: status || undefined, before_id: pageParam || undefined, limit: 100 }),
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.commissions.length === 100 ? last.commissions[99].id : undefined),
  });
  const rows = q.data?.pages.flatMap((p) => p.commissions) ?? [];
  const all = useAllAffiliates();
  const codeOf = useMemo(() => new Map((all.data ?? []).map((a) => [a.id, a.code])), [all.data]);
  const now = Date.now();
  return (
    <Card className="overflow-x-auto">
      <div className="flex flex-wrap items-center gap-2 border-b p-3">
        <Select className="h-8 w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">Any status</option>
          <option value="pending">unpaid</option>
          <option value="paid">paid</option>
          <option value="reversed">taken back (refund)</option>
          <option value="void">void</option>
        </Select>
        <span className="text-xs text-[var(--color-muted-foreground)]">Level 1 is the referring partner; level 2 is the partner who recruited them.</span>
      </div>
      {q.isLoading && <Skeleton className="m-3 h-32" />}
      {q.error && <p className="p-3 text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>}
      {!q.isLoading && !q.error && rows.length === 0 && <Empty title="No commissions match." />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Earned (MT)</TH>
              {!affiliateId && <TH>Partner</TH>}
              <TH>Order</TH>
              <TH>Level</TH>
              <TH className="text-right">Sale</TH>
              <TH className="text-right">Rate</TH>
              <TH className="text-right">Commission</TH>
              <TH>Status</TH>
            </tr>
          </THead>
          <tbody>
            {rows.map((c) => {
              const [label, variant] = COMMISSION_STATUS[c.status] ?? [c.status, "muted" as const];
              const unpaid = !c.payout_id && (c.status === "pending" || c.status === "approved");
              const from = payableFrom(c.earned_at);
              const waiting = unpaid && new Date(from).getTime() > now;
              return (
                <TR key={c.id}>
                  <TD className="num whitespace-nowrap text-xs">{formatDate(c.earned_at)}</TD>
                  {!affiliateId && (
                    <TD>
                      <Link to="/affiliates/$affiliateId" params={{ affiliateId: String(c.affiliate_id) }} className="text-[var(--color-primary)] hover:underline">
                        {codeOf.get(c.affiliate_id) ?? `#${c.affiliate_id}`}
                      </Link>
                    </TD>
                  )}
                  <TD>
                    {c.order_id ? (
                      <Link to="/billing/orders/$orderId" params={{ orderId: String(c.order_id) }} className="num text-[var(--color-primary)] hover:underline">
                        #{c.order_id}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TD>
                  <TD className="num">{c.level}</TD>
                  <TD className="num text-right">{formatCents(c.base_cents)}</TD>
                  <TD className="num text-right">{c.rate_bps != null ? pct(c.rate_bps) : "flat"}</TD>
                  <TD className="num text-right font-medium">{formatCents(c.amount_cents)}</TD>
                  <TD>
                    <Badge variant={variant}>{label}</Badge>
                    {waiting && <div className="text-xs text-[var(--color-muted-foreground)]">payable from {formatDate(from)}</div>}
                    {c.payout_id && <div className="text-xs text-[var(--color-muted-foreground)]">in payout #{c.payout_id}</div>}
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="border-t p-2 text-center">
          <Button size="sm" variant="outline" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            Show more
          </Button>
        </div>
      )}
    </Card>
  );
}

/** Payouts recorded (and the ones Keap logged, marked "from Keap"); with affiliateId, only that partner's. */
export function PayoutsTable({ affiliateId }: { affiliateId?: number }) {
  const q = useQuery({
    queryKey: ["payouts", affiliateId ?? 0],
    queryFn: () => api<{ payouts: Payout[] }>("/api/v1/payouts" + qs({ affiliate_id: affiliateId, limit: 500 })),
  });
  const rows = q.data?.payouts ?? [];
  const all = useAllAffiliates();
  const codeOf = useMemo(() => new Map((all.data ?? []).map((a) => [a.id, a.code])), [all.data]);
  return (
    <Card className="overflow-x-auto">
      {q.isLoading && <Skeleton className="m-3 h-24" />}
      {q.error && <p className="p-3 text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>}
      {!q.isLoading && !q.error && rows.length === 0 && <Empty title="No payouts recorded yet." />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Paid (MT)</TH>
              {!affiliateId && <TH>Partner</TH>}
              <TH className="text-right">Amount</TH>
              <TH>Paid by</TH>
              <TH>Reference</TH>
              <TH>Recorded by</TH>
            </tr>
          </THead>
          <tbody>
            {rows.map((p) => (
              <TR key={p.id}>
                <TD className="num whitespace-nowrap text-xs">{formatDate(p.paid_at)}</TD>
                {!affiliateId && (
                  <TD>
                    <Link to="/affiliates/$affiliateId" params={{ affiliateId: String(p.affiliate_id) }} className="text-[var(--color-primary)] hover:underline">
                      {codeOf.get(p.affiliate_id) ?? `#${p.affiliate_id}`}
                    </Link>
                  </TD>
                )}
                <TD className="num text-right font-medium">{formatCents(p.amount_cents)}</TD>
                <TD className="text-xs">{methodLabel(p.method)}</TD>
                <TD className="max-w-[16rem] text-xs">
                  {p.reference || "—"}
                  {p.note && <div className="truncate text-[var(--color-muted-foreground)]">{p.note}</div>}
                </TD>
                <TD className="text-xs">{p.imported ? <Badge variant="muted">from Keap</Badge> : p.created_by}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function ProgramsTab() {
  const programs = usePrograms();
  const overrides = useQuery({ queryKey: ["affiliate-overrides"], queryFn: () => api<{ overrides: RateOverride[] }>("/api/v1/affiliate-overrides") });
  const products = useQuery({ queryKey: ["products", "all-for-overrides"], queryFn: () => api<{ products: Product[] }>("/api/v1/products" + qs({ limit: 500 })), staleTime: 60_000 });
  const all = useAllAffiliates();
  const productOf = new Map((products.data?.products ?? []).map((p) => [p.id, p.name]));
  const codeOf = new Map((all.data ?? []).map((a) => [a.id, a.code]));
  const members = (id: number) => (all.data ?? []).filter((a) => a.default_program_id === id).length;
  const list = programs.data?.programs ?? [];
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--color-muted-foreground)]">Rates and programs change through Claude, with Brandon's OK first.</p>
      {programs.isLoading && <Skeleton className="h-24" />}
      {programs.error && <p className="text-sm text-[var(--color-danger-fg)]">{programs.error.message}</p>}
      {overrides.error && <LoadError className="px-0" what="the product rates" error={overrides.error} onRetry={() => overrides.refetch()} />}
      {!programs.isLoading && !programs.error && list.length === 0 && (
        <Card>
          <Empty title="No affiliate programs yet." />
        </Card>
      )}
      {list.map((p) => {
        const ov = (overrides.data?.overrides ?? []).filter((o) => o.program_id === p.id);
        return (
          <Card key={p.id} className={p.active ? "" : "row-dim"}>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b p-3">
              <h2 className="font-semibold">{p.name}</h2>
              {!p.active && <Badge variant="muted">off</Badge>}
              {p.keap_id && <span className="text-xs text-[var(--color-muted-foreground)]">from Keap</span>}
              <span className="ml-auto text-sm">
                Level 1 <span className="num font-medium">{pct(p.level1_rate_bps)}</span>, level 2 <span className="num font-medium">{pct(p.level2_rate_bps)}</span>, referral
                counts for <span className="num">{formatInt(p.cookie_days)}</span> days, <span className="num">{members(p.id)}</span> partners
              </span>
              {p.description && <p className="w-full text-sm text-[var(--color-muted-foreground)]">{p.description}</p>}
            </div>
            {ov.length > 0 ? (
              <Table>
                <THead>
                  <tr>
                    <TH>Product with its own rate</TH>
                    <TH>Who</TH>
                    <TH>Level</TH>
                    <TH className="text-right">Rate</TH>
                    <TH>When</TH>
                  </tr>
                </THead>
                <tbody>
                  {ov.map((o) => (
                    <TR key={o.id}>
                      <TD>
                        {productOf.get(o.product_id) ?? `Product #${o.product_id}`}
                        {o.note && <div className="text-xs text-[var(--color-muted-foreground)]">{o.note}</div>}
                      </TD>
                      <TD className="text-xs">{o.affiliate_id ? (codeOf.get(o.affiliate_id) ?? `#${o.affiliate_id}`) : "everyone in the program"}</TD>
                      <TD className="num">{o.level}</TD>
                      <TD className="num text-right">{o.flat_cents != null ? `${formatCents(o.flat_cents)} per sale` : pct(o.rate_bps)}</TD>
                      <TD className="text-xs">{o.starts_at || o.ends_at ? `${o.starts_at ? formatDate(o.starts_at) : "any time"} to ${o.ends_at ? formatDate(o.ends_at) : "no end"}` : "always"}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            ) : overrides.error ? null : (
              <p className="p-3 text-xs text-[var(--color-muted-foreground)]">Every product pays the program's rates.</p>
            )}
          </Card>
        );
      })}
    </div>
  );
}
